import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "sonner";
import coursesService from "../../../api/services/courses";
import certificateService from "../../../api/services/certificate";
import {
  FaArrowLeft,
  FaList,
  FaLock,
  FaCheckCircle,
  FaCertificate,
  FaPlay,
  FaClock,
  FaBookmark,
  FaFilePdf,
} from "react-icons/fa";

// Redux imports
import { selectUser } from "../../../store/slices/auth/slice";
import {
  loadCourseLessons,
  loadLesson,
  checkCertificateEligibility,
} from "../../../store/slices/lessonProgress/asyncThunks";
import {
  selectLessons,
  selectCurrentLesson,
  selectCurrentLessonId,
  selectCourseLessonsLoading,
  selectLessonLoading,
  selectCourseCompleted,
  selectCanApplyForCertificate,
  selectCourseData,
  selectAccessExpired,
  selectAccessExpiryDate,
  selectAccessDenied,
  clearAllData,
} from "../../../store/slices/lessonProgress/slice";

// Components
import VimeoPlayer from "../../../components/VimeoPlayer/VimeoPlayer";

// User Modal Context
import { useUserModal } from "../../../contexts/UserModalContext";

// Video Sidebar Context
import { useVideoSidebar } from "../../../contexts/VideoSidebarContext";

// Certificate imports
import { getCertificateStatus } from "../../../store/slices/certificates/asyncThunks";
import {
  selectCertificateStatus,
  selectApplicationLoading,
} from "../../../store/slices/certificates/slice";
import { FaDownload, FaHourglassHalf } from "react-icons/fa";

// Styles
import {
  PageContainer,
  VideoSection,
  VideoHeader,
  LessonTitle,
  LessonMeta,
  VideoPlayerContainer,
  PlayerLoadingContainer,
  SidebarContainer,
  LessonsListContainer,
  LessonsHeader,
  LessonsList,
  LessonItem,
  LessonIcon,
  LessonInfo,
  LessonTitle as SidebarLessonTitle,
  LessonDuration,
  LessonProgress,
  ProgressBar,
  ProgressFill,
  CertificateSection,
  BackButton,
  LoadingContainer,
  ErrorContainer,
  Row,
  CertificateIcon,
  CertificateIconCircle,
  CertificateTitle,
  CertificateDescription,
  CertificateBtn,
  PdfScriptsSection,
  PdfScriptsTitle,
  PdfScriptsList,
  PdfScriptButton,
  PdfScriptInfo,
  PdfScriptTitle,
  PdfScriptDescription,
  PdfScriptDownloadIcon,
} from "./VideoPage.styles";

const VideoPage = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openCertificateApplicationModal } = useUserModal();
  const { toggleSidebar } = useVideoSidebar();

  const user = useSelector(selectUser);
  const lessons = useSelector(selectLessons);
  const currentLesson = useSelector(selectCurrentLesson);
  const currentLessonId = useSelector(selectCurrentLessonId);
  const courseLessonsLoading = useSelector(selectCourseLessonsLoading);
  const lessonLoading = useSelector(selectLessonLoading);
  const courseCompleted = useSelector(selectCourseCompleted);
  const canApplyForCertificate = useSelector(selectCanApplyForCertificate);
  const courseData = useSelector(selectCourseData);
  const accessExpired = useSelector(selectAccessExpired);
  const accessExpiryDate = useSelector(selectAccessExpiryDate);
  const accessDenied = useSelector(selectAccessDenied);

  // Certificate selectors
  const certificateStatus = useSelector(selectCertificateStatus);
  const applicationLoading = useSelector(selectApplicationLoading);

  // Local state
  const [initializedCourseId, setInitializedCourseId] = useState(null);
  const latestLessonIdRef = useRef(lessonId);
  const navigationTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isInitialized = initializedCourseId === courseId;

  // Initialize course data (and re-initialize when courseId changes,
  // e.g. browser back/forward between lessons of different courses)
  useEffect(() => {
    if (!courseId || initializedCourseId === courseId) {
      return;
    }

    dispatch(clearAllData());
    setInitializedCourseId(courseId);

    // Load all course data in parallel
    Promise.all([
      dispatch(loadCourseLessons(courseId)).unwrap(),
      dispatch(checkCertificateEligibility(courseId)).unwrap(),
    ]).catch((error) => {
      // Za istekao/nedostupan pristup se renderuje namenski ekran — bez toasta
      if (!error?.expired && !error?.accessDenied) {
        toast.error(error?.message || String(error));
      }
    });
  }, [courseId, initializedCourseId, dispatch]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      dispatch(clearAllData());
    };
  }, [dispatch]);

  // Clear pending auto-navigation timer on lesson change / unmount
  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
        navigationTimerRef.current = null;
      }
    };
  }, [lessonId]);

  // Load specific lesson when lessonId changes
  useEffect(() => {
    if (lessonId && isInitialized) {
      latestLessonIdRef.current = lessonId;
      dispatch(loadLesson(lessonId))
        .unwrap()
        .catch((error) => {
          if (latestLessonIdRef.current !== lessonId) {
            return;
          }
          if (!error?.accessDenied && !error?.expired) {
            toast.error(error?.message || String(error));
          }
        });
    }
  }, [lessonId, dispatch, isInitialized]);

  // Load certificate status when eligible
  useEffect(() => {
    if (canApplyForCertificate && courseId && user) {
      dispatch(getCertificateStatus(courseId));
    }
  }, [canApplyForCertificate, courseId, user, dispatch]);

  // Handle lesson selection
  const handleLessonSelect = useCallback(
    (lesson) => {
      if (!lesson.isUnlocked) {
        toast.error("Ova lekcija još uvek nije otključana");
        return;
      }
      navigate(`/korisnicki-panel/kursevi/${courseId}/lekcije/${lesson.id}`);
    },
    [courseId, navigate],
  );

  // Keyboard support for lesson list items
  const handleLessonKeyDown = useCallback(
    (event, lesson) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleLessonSelect(lesson);
      }
    },
    [handleLessonSelect],
  );

  // Handle lesson completion
  // reason: "ended" — video stvarno odgledan do kraja (dozvoljena navigacija)
  // reason: "progress" — server označio završeno kroz periodični save (samo osveži listu)
  const handleLessonCompletion = useCallback(
    (result, reason = "progress") => {
      if (!result?.success || !courseId) {
        return;
      }

      // Reload course lessons to get updated unlock status and progress
      dispatch(loadCourseLessons(courseId))
        .unwrap()
        .then((courseLessonsResult) => {
          // Check certificate eligibility
          if (result.courseCompleted) {
            dispatch(checkCertificateEligibility(courseId));
          }

          // Automatska navigacija isključivo kada je video odgledan do kraja
          if (reason !== "ended") {
            return;
          }

          // Korisnik je u međuvremenu otišao sa lekcije/stranice — ne otimaj navigaciju
          if (
            !mountedRef.current ||
            latestLessonIdRef.current !== currentLessonId
          ) {
            return;
          }

          // Find current lesson index
          const currentIndex = courseLessonsResult.lessons.findIndex(
            (l) => l.id === currentLessonId,
          );

          // Check if there's a next lesson
          const nextLesson = courseLessonsResult.lessons[currentIndex + 1];

          if (nextLesson && nextLesson.isUnlocked) {
            // Navigate to next lesson after short delay
            if (navigationTimerRef.current) {
              clearTimeout(navigationTimerRef.current);
            }
            navigationTimerRef.current = setTimeout(() => {
              navigationTimerRef.current = null;
              if (
                !mountedRef.current ||
                latestLessonIdRef.current !== currentLessonId
              ) {
                return;
              }
              toast.success("Prelazimo na sledeću lekciju!");
              navigate(
                `/korisnicki-panel/kursevi/${courseId}/lekcije/${nextLesson.id}`,
              );
            }, 1500);
          } else if (result.courseCompleted) {
            // All lessons completed - sertifikat se pominje samo ako ga kurs izdaje
            toast.success(
              result.canApplyForCertificate === false
                ? "🎉 Čestitamo! Završili ste ceo kurs!"
                : "🎉 Čestitamo! Završili ste ceo kurs! Sada možete aplicirati za sertifikat.",
            );
          } else {
            // No more lessons or next lesson not unlocked yet
            toast.info(
              "Ova lekcija je završena. Možete nastaviti sa sledećom kada bude dostupna.",
            );
          }
        })
        .catch((error) => {
          console.error("Error reloading course lessons:", error);
          toast.error("Greška prilikom ažuriranja podataka kursa");
        });
    },
    [courseId, currentLessonId, dispatch, navigate],
  );

  // Handle back navigation
  const handleGoBack = useCallback(() => {
    navigate("/korisnicki-panel/kursevi");
  }, [navigate]);

  // Certificate handling
  const handleCertificateAction = useCallback(() => {
    if (!certificateStatus || !certificateStatus.status) {
      openCertificateApplicationModal(courseId, courseData?.title);
    } else if (certificateStatus.status === "approved") {
      const applicationId = certificateStatus.application?.id;
      if (!applicationId) {
        toast.error("Sertifikat trenutno nije dostupan za preuzimanje.");
        return;
      }
      certificateService
        .downloadCertificate(applicationId)
        .then(() => {
          toast.success("Sertifikat je uspešno preuzet!");
        })
        .catch((error) => {
          toast.error(
            error?.message || "Greška prilikom preuzimanja sertifikata",
          );
        });
    }
  }, [
    certificateStatus,
    courseId,
    courseData?.title,
    openCertificateApplicationModal,
  ]);

  // Format duration
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Handle PDF script download
  const handlePdfDownload = useCallback(
    async (scriptName) => {
      try {
        await coursesService.downloadPdfScript(courseId, scriptName);
        toast.success("Skripta je uspešno preuzeta!");
      } catch (error) {
        toast.error("Greška prilikom preuzimanja skripte: " + error.message);
      }
    },
    [courseId],
  );

  // Full-page loader samo za inicijalno učitavanje kursa;
  // promena lekcije prikazuje loader samo u zoni plejera
  if (
    (courseLessonsLoading && lessons.length === 0) ||
    !isInitialized ||
    (lessonLoading && !currentLesson)
  ) {
    return (
      <LoadingContainer>
        <l-line-spinner size="32" stroke="3" speed="1" color="var(--accent)" />
        <p>Učitavam lekcije...</p>
      </LoadingContainer>
    );
  }

  if (accessExpired) {
    return (
      <ErrorContainer>
        <Row>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft />
          </BackButton>
          <h2>Pristup kursu je istekao</h2>
        </Row>
        <p>
          Vaš pristup ovom kursu je istekao
          {accessExpiryDate &&
            ` dana ${new Date(accessExpiryDate).toLocaleDateString("sr-Latn-RS")}`}
          . Za produženje pristupa, molimo kontaktirajte podršku.
        </p>
      </ErrorContainer>
    );
  }

  if (accessDenied) {
    return (
      <ErrorContainer>
        <Row>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft />
          </BackButton>
          <h2>Nemate pristup ovom kursu</h2>
        </Row>
        <p>Da biste gledali lekcije, potrebno je da prvo kupite ovaj kurs.</p>
        <CertificateBtn
          style={{ width: "auto", marginTop: "16px" }}
          onClick={() => navigate(`/kurs/${courseId}`)}
        >
          Pogledaj kurs
        </CertificateBtn>
      </ErrorContainer>
    );
  }

  if (!currentLesson) {
    return (
      <ErrorContainer>
        <Row>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft />
          </BackButton>
          <h2>Lekcija nije pronađena</h2>
        </Row>
        <p>Molimo pokušajte ponovo ili kontaktirajte podršku.</p>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <VideoSection>
        <VideoHeader>
          <div className="header-left">
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft />
            </BackButton>
            <div>
              <LessonTitle>{currentLesson.title}</LessonTitle>
              <LessonMeta>
                <span>
                  <FaClock />
                  {formatDuration(currentLesson.duration || 0)}
                </span>
                <span>
                  <FaBookmark />
                  Lekcija{" "}
                  {lessons.findIndex((l) => l.id === currentLesson.id) +
                    1} od {lessons.length}
                </span>
              </LessonMeta>
            </div>
          </div>
          <button
            className="sidebar-toggle"
            onClick={() =>
              toggleSidebar({
                courseId,
                onLessonSelect: handleLessonSelect,
                onCertificateAction: handleCertificateAction,
              })
            }
          >
            <FaList />
            Lista lekcija
          </button>
        </VideoHeader>

        <VideoPlayerContainer>
          {lessonLoading ? (
            <PlayerLoadingContainer>
              <l-line-spinner size="32" stroke="3" speed="1" color="white" />
              <p>Učitavam lekciju...</p>
            </PlayerLoadingContainer>
          ) : (
            <VimeoPlayer
              vimeoVideoId={currentLesson.vimeoVideoId}
              vimeoHash={currentLesson.vimeoHash}
              lessonId={currentLesson.id}
              initialProgress={currentLesson.progress}
              onCompletion={handleLessonCompletion}
              autoPlay={false}
            />
          )}
        </VideoPlayerContainer>

        {currentLesson.description && (
          <div className="lesson-description">
            <h3>O ovoj lekciji</h3>
            <p>{currentLesson.description}</p>
          </div>
        )}

        {/* PDF Scripts Section */}
        {courseData &&
          courseData?.pdfScripts &&
          courseData?.pdfScripts?.length > 0 && (
            <PdfScriptsSection>
              <PdfScriptsTitle>
                <FaFilePdf color="var(--accent)" />
                Skripte kursa
              </PdfScriptsTitle>
              <PdfScriptsList>
                {courseData?.pdfScripts?.map((script, index) => (
                  <PdfScriptButton
                    key={index}
                    onClick={() => handlePdfDownload(script?.filename)}
                  >
                    <FaFilePdf color="var(--accent)" size={16} />
                    <PdfScriptInfo>
                      <PdfScriptTitle>{script?.title}</PdfScriptTitle>
                      {script?.description && (
                        <PdfScriptDescription>
                          {script?.description}
                        </PdfScriptDescription>
                      )}
                    </PdfScriptInfo>
                    <PdfScriptDownloadIcon>
                      <FaDownload size={14} />
                    </PdfScriptDownloadIcon>
                  </PdfScriptButton>
                ))}
              </PdfScriptsList>
            </PdfScriptsSection>
          )}
      </VideoSection>

      {/* Desktop Sidebar - always visible on larger screens */}
      <SidebarContainer $open={true}>
        <LessonsListContainer>
          <LessonsHeader>
            <Row style={{ marginBottom: 0 }}>
              <FaList color="var(--accent)" />
              <h3>Lista lekcija</h3>
            </Row>
          </LessonsHeader>

          <LessonsList>
            {lessons.map((lesson, index) => (
              <LessonItem
                key={lesson.id}
                role="button"
                tabIndex={0}
                aria-disabled={!lesson.isUnlocked}
                aria-current={lesson.id === currentLessonId ? "true" : undefined}
                $active={lesson.id === currentLessonId}
                $unlocked={lesson.isUnlocked}
                $completed={lesson.progress?.isCompleted}
                onClick={() => handleLessonSelect(lesson)}
                onKeyDown={(e) => handleLessonKeyDown(e, lesson)}
              >
                <LessonIcon
                  $unlocked={lesson.isUnlocked}
                  $completed={lesson.progress?.isCompleted}
                >
                  {!lesson.isUnlocked ? (
                    <FaLock />
                  ) : lesson.progress?.isCompleted ? (
                    <FaCheckCircle />
                  ) : (
                    <FaPlay />
                  )}
                </LessonIcon>

                <LessonInfo>
                  <SidebarLessonTitle $small>
                    {index + 1}. {lesson.title}
                  </SidebarLessonTitle>
                  <LessonDuration>
                    {formatDuration(lesson.duration || 0)}
                  </LessonDuration>
                  {lesson.progress && (
                    <LessonProgress>
                      <ProgressBar>
                        <ProgressFill
                          width={lesson.progress.progressPercentage || 0}
                        />
                      </ProgressBar>
                      <span>
                        {Math.round(lesson.progress.progressPercentage || 0)}%
                      </span>
                    </LessonProgress>
                  )}
                </LessonInfo>
              </LessonItem>
            ))}
          </LessonsList>

          {courseCompleted && canApplyForCertificate && (
            <CertificateSection>
              <CertificateIcon>
                <CertificateIconCircle>
                  {certificateStatus?.status === "approved" ? (
                    <FaDownload />
                  ) : certificateStatus?.status === "pending" ? (
                    <FaHourglassHalf />
                  ) : (
                    <FaCertificate />
                  )}
                </CertificateIconCircle>
              </CertificateIcon>

              {!certificateStatus || !certificateStatus.status ? (
                <>
                  <CertificateTitle>Čestitamo!</CertificateTitle>
                  <CertificateDescription>
                    Uspešno ste završili sve lekcije kursa. Sada možete
                    aplicirati za sertifikat.
                  </CertificateDescription>
                  <CertificateBtn
                    onClick={handleCertificateAction}
                    disabled={applicationLoading}
                  >
                    {applicationLoading ? (
                      <>
                        <l-line-spinner
                          size="18"
                          stroke="2"
                          speed="1"
                          color="var(--surface)"
                        />
                        Apliciranje...
                      </>
                    ) : (
                      <>
                        <FaCertificate />
                        Apliciraj za sertifikat
                      </>
                    )}
                  </CertificateBtn>
                </>
              ) : certificateStatus.status === "pending" ? (
                <>
                  <CertificateTitle>
                    Zahtev za sertifikat je poslat
                  </CertificateTitle>
                  <CertificateDescription>
                    Vaš zahtev za sertifikat je poslat na obradu. Molimo
                    sačekajte dok admin ne obradi vaš rad.
                  </CertificateDescription>
                  <CertificateBtn
                    disabled
                    $status={certificateStatus.status === "pending"}
                  >
                    <FaHourglassHalf />
                    Na obradi...
                  </CertificateBtn>
                </>
              ) : certificateStatus.status === "approved" ? (
                <>
                  <CertificateTitle>Sertifikat odobren!</CertificateTitle>
                  <CertificateDescription>
                    Čestitamo! Vaš sertifikat je odobren. Možete ga sada
                    preuzeti.
                  </CertificateDescription>
                  <CertificateBtn onClick={handleCertificateAction}>
                    <FaDownload />
                    Preuzmi sertifikat
                  </CertificateBtn>
                </>
              ) : null}
            </CertificateSection>
          )}
        </LessonsListContainer>
      </SidebarContainer>
    </PageContainer>
  );
};

export default VideoPage;
